import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../redux';
import {
  savePaymentMethod,
  fetchPaymentMethods,
  deletePaymentMethod,
  setDefaultPaymentMethod,
  setSelectedPaymentMethod,
  clearError,
  selectPaymentMethods,
  selectSelectedPaymentMethod,
  selectDefaultPaymentMethod,
  selectBillingLoading,
  selectBillingSaving,
  selectBillingError,
} from '../../redux/billing/billingSlice';
import { PaymentMethod, SavePaymentMethodRequest } from '../../types/billing';

export interface UsePaymentMethodOptions {
  userId?: string;
  customerId?: string; // Stripe customer ID
  autoFetch?: boolean;
}

export const usePaymentMethod = (options: UsePaymentMethodOptions = {}) => {
  const { userId, customerId, autoFetch = false } = options;
  const dispatch = useDispatch<AppDispatch>();

  // Selectors
  const paymentMethods = useSelector(selectPaymentMethods);
  const selectedPaymentMethod = useSelector(selectSelectedPaymentMethod);
  const defaultPaymentMethod = useSelector(selectDefaultPaymentMethod);
  const isLoading = useSelector(selectBillingLoading);
  const isSaving = useSelector(selectBillingSaving);
  const error = useSelector(selectBillingError);

  // Local state
  const [lastSavedPaymentMethod, setLastSavedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [lastDeletedPaymentMethodId, setLastDeletedPaymentMethodId] = useState<string | null>(null);

  // Auto-fetch payment methods on mount if enabled
  // Only use userId (sub from localStorage)
  useEffect(() => {
    if (autoFetch && userId) {
      dispatch(fetchPaymentMethods({ userId }));
    }
  }, [autoFetch, userId, dispatch]);

  // Save payment method
  const save = useCallback(
    async (request: SavePaymentMethodRequest): Promise<PaymentMethod> => {
      // Include customerId if provided in options or request
      const saveRequest: SavePaymentMethodRequest = {
        ...request,
        customerId: request.customerId || customerId,
      };
      const result = await dispatch(savePaymentMethod(saveRequest)).unwrap();
      setLastSavedPaymentMethod(result);
      // Automatically refresh the list after saving
      // Only use userId (sub from localStorage)
      if (userId) {
        setTimeout(() => {
          dispatch(fetchPaymentMethods({ userId })).catch((err: unknown) => {
            console.error('Error refreshing payment methods after save:', err);
          });
        }, 500);
      }
      return result;
    },
    [dispatch, userId, customerId]
  );

  // Fetch payment methods
  // Only use userId (sub from localStorage)
  const fetch = useCallback(async () => {
    if (!userId) {
      console.warn('⚠️ usePaymentMethod.fetch - userId not provided');
      throw new Error('userId is required to fetch payment methods');
    }
    
    const result = await dispatch(fetchPaymentMethods({ userId })).unwrap();
    return result;
  }, [dispatch, userId]);

  // Delete payment method
  const remove = useCallback(
    async (paymentMethodId: string): Promise<void> => {
      await dispatch(deletePaymentMethod(paymentMethodId)).unwrap();
      setLastDeletedPaymentMethodId(paymentMethodId);
    },
    [dispatch]
  );

  // Set default payment method
  const setDefault = useCallback(
    async (paymentMethodId: string): Promise<void> => {
      if (!userId) {
        throw new Error('userId is required to set default payment method');
      }
      await dispatch(
        setDefaultPaymentMethod({ paymentMethodId, userId })
      ).unwrap();
    },
    [dispatch, userId]
  );

  // Select a payment method (local state only)
  const select = useCallback(
    (paymentMethod: PaymentMethod | null) => {
      dispatch(setSelectedPaymentMethod(paymentMethod));
    },
    [dispatch]
  );

  // Clear error
  const clear = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Get payment method by ID
  const getById = useCallback(
    (id: string): PaymentMethod | undefined => {
      return paymentMethods.find((pm) => pm.id === id);
    },
    [paymentMethods]
  );

  // Get payment method by Stripe ID
  const getByStripeId = useCallback(
    (stripeId: string): PaymentMethod | undefined => {
      return paymentMethods.find(
        (pm) => 'stripePaymentMethodId' in pm && (pm as PaymentMethod & { stripePaymentMethodId?: string }).stripePaymentMethodId === stripeId
      );
    },
    [paymentMethods]
  );

  return {
    // Data
    paymentMethods,
    selectedPaymentMethod,
    defaultPaymentMethod,
    lastSavedPaymentMethod,
    lastDeletedPaymentMethodId,

    // Loading states
    isLoading,
    isSaving,

    // Error
    error,

    // Actions
    save,
    fetch,
    remove,
    setDefault,
    select,
    clear,
    getById,
    getByStripeId,

    // Helpers
    hasPaymentMethods: paymentMethods.length > 0,
    paymentMethodCount: paymentMethods.length,
  };
};
